import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { apiFetch, useApp } from "../../context/AppContext";
import { Modal, Pagination, EmptyState } from "../../components/common/UI";
import {
  ArrowLeftRight,
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  CreditCard,
  Database,
  Globe,
  Loader2,
  Mail,
  RefreshCw,
  RotateCcw,
  Search,
  Settings2,
  ShieldCheck,
  Smartphone,
  User,
  X,
  FileText,
} from "lucide-react";


const TABLE_HEAD_CLASS =
  "text-left text-[10px] uppercase tracking-[0.12em] bg-gradient-to-r from-slate-200/90 to-slate-100/90 text-slate-800 whitespace-nowrap";

function ColumnHeader({ icon: Icon, label, center = false, iconColor = "text-slate-700" }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-bold text-slate-900 ${center ? "justify-center w-full" : ""
        }`}
    >
      {Icon ? <Icon size={12} className={`${iconColor} flex-shrink-0`} strokeWidth={2.25} /> : null}
      {label}
    </span>
  );
}

const cardShell =
  "relative bg-white/60 backdrop-blur-xl rounded-2xl border border-white/50 shadow-lg ring-1 ring-slate-900/[0.04] overflow-hidden";

const premiumTopLine =
  "pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-400/50 to-transparent";

const INITIAL_VARIANCE_ROWS = [
  {
    id: 1,
    dataOwner: "Marketing Department",
    sources: ["Email", "WhatsApp", "Web"],
    consentDataTypes: ["Email Address", "Phone"],
    dspmDataTypes: ["Email Address", "Phone", "PAN"],
    reconsentStatus: "Reconsent Required",
  },
  {
    id: 2,
    dataOwner: "Human Resources",
    sources: ["Email", "WhatsApp", "HR Portal", "Mobile App"],
    consentDataTypes: ["Email Address", "Aadhaar", "PAN", "Phone", "Date of Birth"],
    dspmDataTypes: ["Email Address", "Aadhaar", "PAN", "Phone", "Date of Birth", "Bank Account"],
    reconsentStatus: "Aligned",
  },
  {
    id: 3,
    dataOwner: "Customer Support",
    sources: ["Email", "WhatsApp"],
    consentDataTypes: ["Email Address", "Phone"],
    dspmDataTypes: ["Email Address"],
    reconsentStatus: "Reconsent Pending",
  },
  {
    id: 4,
    dataOwner: "Finance & Billing",
    sources: ["Email", "WhatsApp", "Billing Portal"],
    consentDataTypes: ["Email Address", "PAN"],
    dspmDataTypes: ["Email Address", "PAN", "Phone", "Aadhaar"],
    reconsentStatus: "Reconsent Required",
  },
  {
    id: 5,
    dataOwner: "Product Engineering",
    sources: ["Email"],
    consentDataTypes: ["Email Address"],
    dspmDataTypes: ["Email Address"],
    reconsentStatus: "Reconsent Completed",
  },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses", color: "bg-indigo-500", text: "text-indigo-700", bg: "bg-indigo-50/90 border-indigo-200/90 hover:bg-indigo-100/80" },
  { value: "Reconsent Required", label: "Reconsent Required", color: "bg-rose-500", text: "text-rose-700", bg: "bg-rose-50/90 border-rose-200/90 hover:bg-rose-100/80" },
  { value: "Initiated", label: "Initiated", color: "bg-sky-500", text: "text-sky-700", bg: "bg-sky-50/90 border-sky-200/90 hover:bg-sky-100/80" },
  { value: "Aligned", label: "Aligned", color: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50/90 border-emerald-200/90 hover:bg-emerald-100/80" },
];

function StatusFilterDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentOption = STATUS_OPTIONS.find((opt) => opt.value === value) || STATUS_OPTIONS[0];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-bold shadow-sm transition-all duration-200 hover:shadow ${currentOption.bg} ${currentOption.text}`}
      >
        <span className="text-[10px] uppercase tracking-wider font-extrabold opacity-60">Status:</span>
        <span className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${currentOption.color} animate-pulse`} />
          {currentOption.label}
        </span>
        <ChevronDown size={13} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-1.5 w-48 bg-white/95 backdrop-blur-xl rounded-xl border border-slate-200 shadow-xl ring-1 ring-slate-900/5 z-50 p-1.5 space-y-1"
          >
            {STATUS_OPTIONS.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    isSelected
                      ? `${opt.bg} ${opt.text}`
                      : "text-slate-700 hover:bg-slate-100/70 hover:text-slate-900"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${opt.color}`} />
                    {opt.label}
                  </span>
                  {isSelected && <ShieldCheck size={13} className={opt.text} />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function IntegrationStatusBadge({ label, active, loading }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/80 border border-slate-200/80 shadow-sm animate-pulse">
        <div className="w-2 h-2 rounded-full bg-slate-300" />
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400 leading-none">{label}</p>
          <p className="text-xs font-bold text-slate-300 mt-0.5 w-14 h-3 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  const activeStyles = active
    ? "border-emerald-200/80 bg-emerald-50/80"
    : "border-slate-200/80 bg-white/80";
  const dotColor = active ? "bg-emerald-500" : "bg-slate-400";
  const statusText = active ? "Active" : "Inactive";
  const statusColor = active ? "text-emerald-700" : "text-slate-500";

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border shadow-sm ${activeStyles}`}>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor} ${active ? "animate-pulse" : ""}`} />
      <div>
        <p className="text-[9px] font-bold uppercase tracking-wide text-slate-600 leading-none">{label}</p>
        <p className={`text-xs font-bold mt-0.5 ${statusColor}`}>{statusText}</p>
      </div>
    </div>
  );
}
const CANONICAL_TYPES = {
  email: "Email Address",
  phone: "Phone",
  pan: "PAN",
  aadhaar: "Aadhaar",
};

function normalizeDataType(raw) {
  const label = (raw || "").trim();
  const lower = label.toLowerCase();
  if (lower.includes("email") || lower.includes("e-mail")) {
    return CANONICAL_TYPES.email;
  }
  if (lower.includes("phone") || lower.includes("mobile") || lower.includes("telephone")) {
    return CANONICAL_TYPES.phone;
  }
  if (lower === "pan" || lower.includes(" permanent account")) {
    return CANONICAL_TYPES.pan;
  }
  if (lower.includes("aadhaar") || lower.includes("aadhar") || lower.includes("adhar") || lower === "uid") {
    return CANONICAL_TYPES.aadhaar;
  }
  return label;
}

function computeVarianceTypes(consentTypes = [], dspmTypes = []) {
  const normalizedConsent = (consentTypes || [])
    .map((t) => normalizeDataType(t))
    .filter((t) => t && t.trim());
  const normalizedDspm = (dspmTypes || [])
    .map((t) => normalizeDataType(t))
    .filter((t) => t && t.trim());

  const consentSet = new Set(normalizedConsent);

  const variance = [];
  for (const t of normalizedDspm) {
    if (!consentSet.has(t) && !variance.includes(t)) {
      variance.push(t);
    }
  }
  return variance.sort();
}

function formatDateTime(ts) {
  if (!ts) return "—";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatUserFriendlyErrorMessage(errMsg) {
  if (!errMsg) return "";
  
  const lowerMsg = errMsg.toLowerCase();
  
  if (lowerMsg.includes("no consentrecord found")) {
    const ownerMatch = errMsg.match(/data_owner=([^,\s]+)/);
    const typeMatch = errMsg.match(/consent_type=([^,\s.]+)/);
    
    const owner = ownerMatch ? ownerMatch[1] : "";
    const type = typeMatch ? typeMatch[1] : "";
    
    if (owner && type) {
      return `No matching consent record found for "${owner}" with consent type "${type}". Please check the database to ensure this record exists.`;
    }
    return "No matching consent record found in the database for the specified data owner and consent type.";
  }
  
  return errMsg;
}

const VISIBLE_DATA_TYPES = 3;

function WhatsAppIcon({ size = 12, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.881 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

const SOURCE_ICON_META = {
  email: { Icon: Mail, color: "text-sky-600" },
  whatsapp: { Icon: WhatsAppIcon, color: "text-emerald-600", custom: true },
  web: { Icon: Globe, color: "text-violet-600" },
  "hr portal": { Icon: Building2, color: "text-orange-600" },
  "mobile app": { Icon: Smartphone, color: "text-indigo-600" },
  "billing portal": { Icon: CreditCard, color: "text-amber-600" },
};

function SourceTag({ source, large = false }) {
  const iconSize = large ? 14 : 12;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md font-semibold border bg-sky-50 text-sky-700 border-sky-100 ${large ? "px-3 py-2 text-sm" : "px-2 py-0.5 text-[11px]"
        }`}
    >
      <SourceIcon source={source} size={iconSize} />
      {source}
    </span>
  );
}

function SourceIcon({ source, size = 12 }) {
  const key = source.trim().toLowerCase();
  const meta = SOURCE_ICON_META[key] || { Icon: Globe, color: "text-slate-500" };
  const { Icon, color, custom } = meta;

  if (custom) {
    return <Icon size={size} className={`flex-shrink-0 ${color}`} />;
  }

  return <Icon size={size} className={`flex-shrink-0 ${color}`} aria-hidden="true" />;
}

function SourcesModal({ open, onClose, dataOwner, sources }) {
  return (
    <Modal open={open} onClose={onClose} title={`Sources — ${dataOwner}`} size="sm">
      <p className="text-sm text-slate-600 mb-4">
        All consent collection sources for this data owner.
      </p>
      <div className="flex flex-wrap gap-2">
        {sources.map((source) => (
          <SourceTag key={source} source={source} large />
        ))}
      </div>
    </Modal>
  );
}

function SourceTags({ sources, dataOwner }) {
  const [modalOpen, setModalOpen] = useState(false);

  if (!sources?.length) {
    return <span className="text-xs text-slate-400 italic">No sources</span>;
  }

  const hasMore = sources.length > VISIBLE_DATA_TYPES;
  const visibleSources = hasMore ? sources.slice(0, VISIBLE_DATA_TYPES) : sources;
  const hiddenCount = sources.length - VISIBLE_DATA_TYPES;

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {visibleSources.map((source) => (
          <SourceTag key={source} source={source} />
        ))}
        {hasMore && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 underline-offset-2 hover:underline"
          >
            View more (+{hiddenCount})
          </button>
        )}
      </div>
      <SourcesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        dataOwner={dataOwner}
        sources={sources}
      />
    </>
  );
}

function DataTypesModal({ open, onClose, dataOwner, types, variant = "consent" }) {
  const title =
    variant === "consent"
      ? "Consent Data Types"
      : variant === "dspm"
        ? "DSPM Data Types"
        : "Variance Data Types";

  const description =
    variant === "consent"
      ? `All consent data types registered for ${dataOwner}.`
      : variant === "dspm"
        ? `All DSPM-discovered data types for ${dataOwner}.`
        : `All data type variances for ${dataOwner}.`;

  const styles =
    variant === "consent"
      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
      : variant === "dspm"
        ? "bg-indigo-50 text-indigo-700 border-indigo-100"
        : "bg-amber-50 text-amber-700 border-amber-100";

  return (
    <Modal open={open} onClose={onClose} title={`${title} — ${dataOwner}`} size="sm">
      <p className="text-sm text-slate-600 mb-4">{description}</p>
      <div className="flex flex-wrap gap-2">
        {types.map((type) => (
          <span
            key={type}
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border ${styles}`}
          >
            {type}
          </span>
        ))}
      </div>
    </Modal>
  );
}

function DataTypeTags({ types, variant = "consent", dataOwner }) {
  const [modalOpen, setModalOpen] = useState(false);

  if (!types?.length) {
    const label =
      variant === "consent"
        ? "No consent data types"
        : variant === "dspm"
          ? "No data detected"
          : "No variance";
    return <span className="text-xs text-slate-400 italic">{label}</span>;
  }

  const styles =
    variant === "consent"
      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
      : variant === "dspm"
        ? "bg-indigo-50 text-indigo-700 border-indigo-100"
        : "bg-amber-50 text-amber-700 border-amber-100";

  const hasMore = types.length > VISIBLE_DATA_TYPES;
  const visibleTypes = hasMore ? types.slice(0, VISIBLE_DATA_TYPES) : types;
  const hiddenCount = types.length - VISIBLE_DATA_TYPES;

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {visibleTypes.map((type) => (
          <span
            key={type}
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${styles}`}
          >
            {type}
          </span>
        ))}
        {hasMore && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 underline-offset-2 hover:underline"
          >
            View more (+{hiddenCount})
          </button>
        )}
      </div>
      <DataTypesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        dataOwner={dataOwner}
        types={types}
        variant={variant}
      />
    </>
  );
}

function DeletionDetailsModal({ open, onClose, dataOwner, deletedTotal, deletedByType, message }) {
  return (
    <Modal open={open} onClose={onClose} title={`Deletion Summary — ${dataOwner}`} size="sm">
      <div className="space-y-4">
        {/* Full Message card */}
        <div className="p-3 bg-amber-50/60 border border-amber-200/60 rounded-xl">
          <p className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-1">
            System Message
          </p>
          <p className="text-sm text-slate-800 font-medium leading-relaxed">
            {message}
          </p>
        </div>

        {/* Total Count */}
        <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-100 rounded-xl">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Total Records Deleted</span>
          <span className="text-lg font-extrabold text-slate-900 tabular-nums">{deletedTotal ?? 0}</span>
        </div>

        {/* Breakdown table/list */}
        <div>
          <p className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-2.5">
            Breakdown by Data Type
          </p>
          {deletedByType && deletedByType.length > 0 ? (
            <div className="border border-slate-200/60 rounded-xl overflow-hidden shadow-sm bg-white">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-100 text-left">
                    <th className="px-4 py-2.5">Data Type</th>
                    <th className="px-4 py-2.5 text-right">Deleted Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                  {deletedByType.map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50">
                      <td className="px-4 py-2.5 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                        {item.data_type}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-bold text-slate-900">
                        {item.deleted}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic bg-white p-4 text-center rounded-xl border border-slate-150 shadow-sm">
              No detailed breakdown available
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

function ReconsentStatusBadge({ status, onClick, hasDetails }) {
  const metaMap = {
    "Reconsent Required": {
      cls: "bg-rose-50/90 text-rose-700 border-rose-200/90",
      dot: "bg-rose-500",
    },
    "Reconsent Pending": {
      cls: "bg-amber-50/90 text-amber-700 border-amber-200/90",
      dot: "bg-amber-500",
    },
    Initiated: {
      cls: "bg-sky-50/90 text-sky-700 border-sky-200/90",
      dot: "bg-sky-500",
    },
    "Reconsent Completed": {
      cls: "bg-emerald-50/90 text-emerald-700 border-emerald-200/90",
      dot: "bg-emerald-500",
    },
    Aligned: {
      cls: "bg-emerald-50/90 text-emerald-700 border-emerald-200/90",
      dot: "bg-emerald-500",
    },
  };

  const meta = metaMap[status] || {
    cls: "bg-rose-50/90 text-rose-700 border-rose-200/90",
    dot: "bg-rose-500",
  };
  const isStandard = status in metaMap;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!hasDetails && isStandard}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold border whitespace-nowrap text-left transition-all ${
        hasDetails || !isStandard
          ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-sm focus:outline-none"
          : "cursor-default"
      } ${meta.cls}`}
    >
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot} ${status === "Initiated" || status === "Reconsent Pending" ? "animate-pulse" : ""}`} />
      <span className="truncate max-w-[120px]" title={status}>
        {status}
      </span>
      {(hasDetails || !isStandard) && (
        <span className="ml-1 text-[9px] px-1.5 py-0.5 bg-rose-600 text-white rounded font-extrabold uppercase tracking-wider shadow-sm animate-pulse">
          Details
        </span>
      )}
    </button>
  );
}

function ReinitiateVarianceButton({ disabled, loading, onClick, status }) {
  const isInitiated = status === "Initiated";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading || isInitiated}
      title={isInitiated ? "Reconsent has been initiated" : disabled ? "No variance to reinitiate" : "Reinitiate variance check"}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-all ${isInitiated
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 disabled:opacity-100"
          : "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300 disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-indigo-50"
        }`}
    >
      {loading ? (
        <Loader2 size={12} className="animate-spin flex-shrink-0" />
      ) : isInitiated ? (
        <ShieldCheck size={12} className="flex-shrink-0" />
      ) : (
        <RotateCcw size={12} className="flex-shrink-0" />
      )}
      {isInitiated ? "Initiated" : "Reinitiate"}
    </button>
  );
}

function RowCheckbox({ checked, indeterminate, onChange, ariaLabel }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = Boolean(indeterminate);
      }}
      onChange={onChange}
      aria-label={ariaLabel}
      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/40 cursor-pointer w-3.5 h-3.5"
    />
  );
}

function isIntegrationActive(record, { payloadKey, countKey } = {}) {
  if (!record) return false;
  if (payloadKey && Array.isArray(record[payloadKey]) && record[payloadKey].length > 0) return true;
  if (countKey != null && (record[countKey] ?? 0) > 0) return true;
  if ((record.total ?? 0) > 0) return true;
  if (record.updated_at || record.received_at) return true;
  return false;
}


function LogItem({ log }) {
  const [expanded, setExpanded] = useState(false);

  const actionConfig = {
    CONSENT: {
      label: "Consent Collected",
      className: "bg-blue-50 text-blue-700 border-blue-200",
    },
    DSPM: {
      label: "DSPM Scan",
      className: "bg-violet-50 text-violet-700 border-violet-200",
    },
    DELETION_SUMMARY: {
      label: "Delete Summary",
      className: "bg-rose-50 text-rose-700 border-rose-200/80",
    },
    REINITIATE: {
      label: "Reconsent Initiated",
      className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    VARIANCE_UPDATE: {
      label: "Variance Updated",
      className: "bg-indigo-50 text-indigo-700 border-indigo-200",
    },
  };

  const act = actionConfig[log.action] || {
    label: log.action || "Update Scan",
    className: "bg-slate-50 text-slate-700 border-slate-200",
  };

  const actionColors = act.className;

  const statusColors =
    log.status === "success"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-rose-50 text-rose-700 border-rose-200";

  // Parse payload data for clean visual display
  const payload = log.payload || {};
  const isConsent = log.action === "CONSENT" || log.action === "REINITIATE" || log.action === "VARIANCE_UPDATE";

  const isDataOwnerMismatch = log.status === "failed" && 
    (log.error_message?.toLowerCase().includes("no consentrecord found") || 
     log.error_message?.toLowerCase().includes("data_owner") ||
     log.error_message?.toLowerCase().includes("data owner"));

  return (
    <div className="border border-slate-200/80 rounded-xl bg-white shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md">
      {/* Summary line clickable to expand */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50/50 transition-colors focus:outline-none"
      >
        <div className="flex flex-wrap items-center gap-3">
          {/* Timestamp */}
          <span className="text-xs font-semibold text-slate-500 tabular-nums">
            {formatDateTime(log.created_at)}
          </span>

          {/* Action Badge */}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border ${actionColors}`}>
            {act.label}
          </span>

          {/* Status Badge */}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border capitalize ${statusColors}`}>
            {log.status === "success" ? "Success" : "Failed"}
          </span>

          {isDataOwnerMismatch && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold border bg-amber-50 text-amber-700 border-amber-200">
              Data Owner Mismatch
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-slate-400">
          <span className="text-[10px] font-medium text-slate-500">
            {log.triggered_by || "System"}
          </span>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {/* Expanded payload / error section */}
      {expanded && (
        <div className="px-4 pb-4 pt-3 border-t border-slate-100 bg-slate-50/40 space-y-4">
          {/* Error Message Section */}
          {log.error_message && (
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-rose-600 mb-1">
                Reason for Failure
              </p>
              <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg text-xs text-rose-800 font-medium break-all md:break-words">
                {formatUserFriendlyErrorMessage(log.error_message)}
              </div>
            </div>
          )}

          {/* User-friendly Change Details */}
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-2">
              Details of Update
            </p>

            {log.action === "DELETION_SUMMARY" ? (
              <div className="bg-white border border-slate-150 rounded-xl p-3 space-y-2.5 shadow-sm">
                {(() => {
                  const innerPayload = payload.payload || {};
                  const summaries = innerPayload.deletion_summary || [];
                  if (summaries.length === 0) {
                    return <span className="text-xs text-slate-400 italic">No deletion details available</span>;
                  }
                  return summaries.map((item, idx) => (
                    <div key={idx} className={`${idx > 0 ? "border-t border-slate-100 pt-2.5" : ""}`}>
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-400 font-medium">Deleted Total</span>
                        <span className="text-rose-600 font-bold tabular-nums">{item.deleted_total} records</span>
                      </div>
                      {item.message && (
                        <p className="text-xs text-slate-650 mt-1 font-medium bg-slate-50 p-2 rounded-lg border border-slate-100">
                          {item.message}
                        </p>
                      )}
                      {Array.isArray(item.deleted_by_type) && item.deleted_by_type.length > 0 && (
                        <div className="mt-2.5">
                          <span className="text-slate-400 block text-[10px] font-medium mb-1.5">Deleted By Type</span>
                          <div className="flex flex-wrap gap-1.5">
                            {item.deleted_by_type.map((t, tIdx) => (
                              <span key={tIdx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border bg-rose-50 text-rose-700 border-rose-100">
                                <span className="font-bold">{t.data_type}:</span>
                                <span className="tabular-nums font-extrabold">{t.deleted}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ));
                })()}
              </div>
            ) : isConsent ? (
              <div className="bg-white border border-slate-150 rounded-xl p-3 space-y-2.5 shadow-sm">
                <div className="grid grid-cols-3 gap-4 text-xs">
                  <div className="min-w-0">
                    <span className="text-slate-400 block font-medium">Source / Channel</span>
                    <span className="text-slate-800 font-bold mt-0.5 block break-all">
                      {payload.consent_source || payload["consent source"] || "—"}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-slate-400 block font-medium">Type of Consent</span>
                    <span className="text-slate-800 font-bold mt-0.5 block break-all">
                      {payload.consent_type || payload["consent type"] || "—"}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-slate-400 block font-medium">Data Owner</span>
                    <span className={`font-bold mt-0.5 block break-all ${
                      isDataOwnerMismatch ? "text-rose-600 font-extrabold" : "text-slate-800"
                    }`}>
                      {log.data_owner || payload.data_owner || payload["data owner"] || "—"}
                    </span>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-2">
                  <span className="text-slate-400 block text-xs font-medium mb-1.5">Registered Data Types</span>
                  <div className="flex flex-wrap gap-1">
                    {(payload.data_types || payload["data types"] || []).length > 0 ? (
                      (payload.data_types || payload["data types"]).map((t) => (
                        <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-100">
                          {t}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400 italic">No data types registered</span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-slate-155 rounded-xl p-3 space-y-3 shadow-sm">
                {/* List Data Owners and Data Types from DSPM */}
                {(() => {
                  const innerPayload = payload.payload || {};
                  const list = innerPayload.data_owner_data_types || [];
                  if (list.length === 0) {
                    return <span className="text-xs text-slate-400 italic">No data types scanned</span>;
                  }
                  return list.map((item, idx) => (
                    <div key={idx} className={`${idx > 0 ? "border-t border-slate-100 pt-2.5" : ""}`}>
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-400 font-medium">Data Owner</span>
                        <span className="text-slate-800 font-bold">{item.data_owner}</span>
                      </div>
                      <div className="mt-1.5">
                        <span className="text-slate-400 block text-[10px] font-medium mb-1">Discovered Data Types</span>
                        <div className="flex flex-wrap gap-1">
                          {(item.data_types || []).map((t) => (
                            <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border bg-indigo-50 text-indigo-700 border-indigo-100">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


export default function ConsentVariance() {
  const { showNotification } = useApp();
  const [varianceRows, setVarianceRows] = useState([]);
  const [consentStats, setConsentStats] = useState(null);
  const [dspmStats, setDspmStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reinitiatingId, setReinitiatingId] = useState(null);
  const [isBulkReinitiating, setIsBulkReinitiating] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // Logs Drawer states
  const [showLogsDrawer, setShowLogsDrawer] = useState(false);
  const [selectedOwnerForLogs, setSelectedOwnerForLogs] = useState(null);
  const [logsList, setLogsList] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [drawerPage, setDrawerPage] = useState(1);
  const [drawerPages, setDrawerPages] = useState(1);
  const [drawerTotal, setDrawerTotal] = useState(0);
  const DRAWER_PAGE_SIZE = 5;

  // Deletion Modal states
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [selectedRowForDeletion, setSelectedRowForDeletion] = useState(null);

  const handleOpenDeletionModal = useCallback((row) => {
    setSelectedRowForDeletion(row);
    setShowDeletionModal(true);
  }, []);

  const fetchLogs = useCallback(async (owner, targetPage = 1) => {
    setLogsLoading(true);
    setLogsList([]);
    try {
      const params = new URLSearchParams({
        data_owner: owner,
        page: String(targetPage),
        page_size: String(DRAWER_PAGE_SIZE),
      });
      const res = await apiFetch(`/consent-variance/logs?${params}`);
      if (res.ok) {
        const data = await res.json();
        setLogsList(data.items || []);
        setDrawerTotal(data.total || 0);
        setDrawerPages(data.pages || 1);
        setDrawerPage(data.page || 1);
      } else {
        showNotification("Failed to load activity logs", "error");
      }
    } catch {
      showNotification("Failed to load activity logs", "error");
    } finally {
      setLogsLoading(false);
    }
  }, [showNotification]);

  const handleOpenLogsDrawer = useCallback((owner) => {
    setSelectedOwnerForLogs(owner);
    setShowLogsDrawer(true);
    setDrawerPage(1);
    fetchLogs(owner, 1);
  }, [fetchLogs]);


  // Pagination states
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 10;

  // Search & Filter states
  const [dataOwnerSearch, setDataOwnerSearch] = useState("");
  const [debouncedDataOwnerSearch, setDebouncedDataOwnerSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Debouncing effect for Data Owner search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedDataOwnerSearch(dataOwnerSearch);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [dataOwnerSearch]);

  const allSelected =
    varianceRows.length > 0 && selectedIds.length === varianceRows.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.length === varianceRows.length ? [] : varianceRows.map((row) => row.id),
    );
  }, [varianceRows]);

  const toggleSelectRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  }, []);

  const handleReinitiateVariance = useCallback(
    async (row) => {
      // Prefer backend-computed variance; fall back to local computation
      const varianceTypes =
        row.varianceTypes && row.varianceTypes.length > 0
          ? row.varianceTypes
          : computeVarianceTypes(row.consentDataTypes, row.dspmDataTypes);
      if (!varianceTypes.length) return;

      setReinitiatingId(row.id);
      try {
        const res = await apiFetch(`/consent-variance/${row.id}/reinitiate`, {
          method: "POST",
        });
        if (!res.ok) {
          throw new Error("Failed to reinitiate");
        }
        const data = await res.json();

        setVarianceRows((prev) =>
          prev.map((item) =>
            item.id === row.id
              ? { ...item, reconsentStatus: data.reconsent_status || "Initiated" }
              : item,
          ),
        );
        showNotification(`Variance reinitiated for ${row.dataOwner}`, "success");
      } catch {
        showNotification(`Failed to reinitiate variance for ${row.dataOwner}`, "error");
      } finally {
        setReinitiatingId(null);
      }
    },
    [showNotification],
  );

  const handleReinitiateSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;

    // Filter only selected rows that have variance
    const selectedRows = varianceRows.filter((row) => selectedIds.includes(row.id));
    const rowsToReinitiate = selectedRows.filter((row) => {
      const varianceTypes =
        row.varianceTypes && row.varianceTypes.length > 0
          ? row.varianceTypes
          : computeVarianceTypes(row.consentDataTypes, row.dspmDataTypes);
      return varianceTypes.length > 0 && row.reconsentStatus !== "Initiated";
    });

    if (rowsToReinitiate.length === 0) {
      showNotification("No selected rows require reinitiation", "info");
      return;
    }

    setIsBulkReinitiating(true);
    let succeeded = 0;
    let failed = 0;

    try {
      await Promise.all(
        rowsToReinitiate.map(async (row) => {
          try {
            const res = await apiFetch(`/consent-variance/${row.id}/reinitiate`, {
              method: "POST",
            });
            if (!res.ok) throw new Error("Failed");
            const data = await res.json();

            setVarianceRows((prev) =>
              prev.map((item) =>
                item.id === row.id
                  ? { ...item, reconsentStatus: data.reconsent_status || "Initiated" }
                  : item,
              ),
            );
            succeeded++;
          } catch {
            failed++;
          }
        }),
      );

      if (failed === 0) {
        showNotification(`Successfully reinitiated ${succeeded} variances`, "success");
        setSelectedIds([]); // Clear selection on complete success
      } else if (succeeded > 0) {
        showNotification(`Reinitiated ${succeeded} variances, ${failed} failed`, "warning");
      } else {
        showNotification("Failed to reinitiate selected variances", "error");
      }
    } catch {
      showNotification("Failed to reinitiate selected variances", "error");
    } finally {
      setIsBulkReinitiating(false);
    }
  }, [selectedIds, varianceRows, showNotification]);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (debouncedDataOwnerSearch.trim()) {
        params.append("search", debouncedDataOwnerSearch.trim());
      }
      if (statusFilter && statusFilter !== "all") {
        params.append("reconsent_status", statusFilter);
      }

      const [consentRes, dspmRes, varianceRes] = await Promise.all([
        apiFetch("/consent/stats"),
        apiFetch("/dspm/stats"),
        apiFetch(`/consent-variance?${params}`),
      ]);
      if (consentRes.ok) setConsentStats(await consentRes.json());
      if (dspmRes.ok) setDspmStats(await dspmRes.json());
      if (varianceRes.ok) {
        const varData = await varianceRes.json();
        const rows = (varData.items || []).map((item) => ({
          id: item.id,
          dataOwner: item.data_owner || "Unknown Owner",
          // consent_source can be a comma-separated string; split into individual tags
          sources: item.consent_source
            ? item.consent_source.split(",").map((s) => s.trim()).filter(Boolean)
            : [],
          consentDataTypes: item.data_types || [],
          dspmDataTypes: item.dspm_data_types || [],
          // Use backend-computed variance directly (avoids normalisation drift)
          varianceTypes: item.variance || [],
          reconsentStatus: item.reconsent_status || "Aligned",
          deletedTotal: item.deleted_total,
          deletedByType: item.deleted_by_type || [],
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          organisationId: item.organisation_id,
          module: item.module,
          consentType: item.consent_type,
          consentSource: item.consent_source,
        }));
        setVarianceRows(rows);
        setTotal(varData.total || 0);
        setPages(varData.pages || 1);
      }
    } catch {
      // keep prior values
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, debouncedDataOwnerSearch, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const consentActive = useMemo(
    () => isIntegrationActive(consentStats, { countKey: "total" }),
    [consentStats],
  );
  const dspmActive = useMemo(
    () =>
      isIntegrationActive(dspmStats, {
        payloadKey: "payload",
        countKey: "total_data_count",
      }),
    [dspmStats],
  );

  const varianceCount = useMemo(
    () =>
      varianceRows.filter(
        (row) =>
          row.reconsentStatus === "Reconsent Required" ||
          row.reconsentStatus === "Reconsent Pending",
      ).length,
    [varianceRows],
  );

  return (
    <div
      className="min-h-full -m-4 lg:-m-6 font-sans relative overflow-hidden bg-slate-50"
      style={{
        background:
          "linear-gradient(160deg, #f8fafc 0%, #eff6ff 30%, #eef2ff 55%, #f0f9ff 100%)",
      }}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 65%)",
          }}
        />
      </div>

      <div className="relative z-10 space-y-4 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Section 1 — Header with integration status */}
        <motion.section
          data-tour-id="tour-consent-header"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className={`${cardShell} p-5 sm:p-6`}
        >
          <div className={premiumTopLine} />
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-3 sm:gap-4 min-w-0">
              <div
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: "linear-gradient(135deg, #6366f1, #0ea5e9)",
                  boxShadow: "0 8px 24px rgba(99,102,241,0.25)",
                }}
              >
                <ArrowLeftRight size={20} className="text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 font-display tracking-tight">
                  Consent Variance
                </h1>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                  Compare consent records against DSPM-discovered data types by data owner and track
                  reconsent actions where variances exist.
                </p>
                {!loading && varianceCount > 0 && (
                  <p className="text-xs font-semibold text-amber-700 mt-2">
                    {varianceCount} data owner{varianceCount !== 1 ? "s" : ""} require reconsent
                  </p>
                )}
              </div>
            </div>

            <div data-tour-id="tour-consent-integrations" className="flex flex-wrap items-center gap-2 sm:gap-3 sm:justify-end flex-shrink-0">
              <IntegrationStatusBadge label="Consent" active={consentActive} loading={loading} />
              <IntegrationStatusBadge label="DSPM" active={dspmActive} loading={loading} />
              <button
                type="button"
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-white/80 border border-slate-200/80 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-60"
              >
                <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>
        </motion.section>

        {/* Section 2 — Variance table */}
        <motion.section
          data-tour-id="tour-consent-table"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.08 }}
          className={cardShell}
        >
          <div className={premiumTopLine} />
          <div className="px-5 sm:px-6 py-4 border-b border-slate-200/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Database size={16} className="text-indigo-500" />
              <h2 className="text-sm font-bold text-slate-900 font-display">Consent Variance</h2>
            </div>
            <div className="flex items-center gap-3">
              {/* Vibrant Status Filter Dropdown */}
              <StatusFilterDropdown
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter(val);
                  setPage(1);
                }}
              />

              {/* Styled Search Box */}
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500" />
                <input
                  type="text"
                  className="w-48 sm:w-64 pl-8 pr-7 py-1.5 bg-white/90 border border-slate-200/90 rounded-xl text-xs font-semibold text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-500 transition-all placeholder:text-slate-400 placeholder:font-normal"
                  placeholder="Search By Data Owner, Source..."
                  value={dataOwnerSearch}
                  onChange={(e) => setDataOwnerSearch(e.target.value)}
                />
                {dataOwnerSearch && (
                  <button
                    type="button"
                    onClick={() => setDataOwnerSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>

              {selectedIds.length > 0 && (
                <div className="flex items-center gap-2 border-l border-slate-200 pl-2">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-600 ml-1">
                    {selectedIds.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={handleReinitiateSelected}
                    disabled={isBulkReinitiating}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-white bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 shadow-sm transition-all disabled:opacity-60 cursor-pointer focus:outline-none"
                  >
                    {isBulkReinitiating ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <RotateCcw size={12} />
                    )}
                    Reinitiate Selected
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 sm:p-5">

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
                <Loader2 size={32} className="animate-spin text-indigo-500" />
                <span className="text-sm font-medium">Loading records...</span>
              </div>
            ) : varianceRows.length === 0 ? (
              <div className="py-16">
                <EmptyState
                  icon={<ArrowLeftRight size={44} className="text-indigo-500" />}
                  title="No consent variance records"
                  description="There are no consent variance records stored for this organisation. Records will appear here when consent data payloads are submitted."
                />
              </div>
            ) : (
              <div className="overflow-x-auto no-scrollbar rounded-xl border border-slate-200/80 ring-1 ring-slate-900/[0.03]">
                <table className="w-full text-sm min-w-[1150px]">
                  <thead>
                    <tr className={TABLE_HEAD_CLASS}>
                      <th className="px-3 py-3.5 font-bold w-[4%] text-center">
                        <RowCheckbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleSelectAll}
                          ariaLabel="Select all rows"
                        />
                      </th>
                      <th className="px-3 py-3.5 font-bold w-[4%] text-center">
                        <ColumnHeader label="S.No." center />
                      </th>
                      <th className="px-4 py-3.5 font-bold w-[14%]">
                        <ColumnHeader icon={User} label="Data Owner" iconColor="text-indigo-600" />
                      </th>
                      <th className="px-4 py-3.5 font-bold w-[14%]">
                        <ColumnHeader icon={Globe} label="Source" iconColor="text-sky-600" />
                      </th>
                      <th className="px-4 py-3.5 font-bold w-[12%]">
                        <ColumnHeader icon={FileText} label="Consent Type" iconColor="text-blue-600" />
                      </th>
                      <th className="px-4 py-3.5 font-bold w-[13%]">
                        <ColumnHeader icon={ShieldCheck} label="Consent Data Types" iconColor="text-emerald-600" />
                      </th>
                      <th className="px-4 py-3.5 font-bold w-[13%]">
                        <ColumnHeader icon={Database} label="DSPM Data Types" iconColor="text-violet-600" />
                      </th>
                      <th className="px-4 py-3.5 font-bold w-[13%]">
                        <ColumnHeader icon={ArrowLeftRight} label="Variance" iconColor="text-amber-600" />
                      </th>
                      <th className="px-4 py-3.5 font-bold w-[10%]">
                        <ColumnHeader icon={ClipboardCheck} label="Reconsent Status" iconColor="text-rose-600" />
                      </th>
                      <th className="px-4 py-3.5 font-bold w-[12%]">
                        <ColumnHeader icon={Calendar} label="Created On" iconColor="text-teal-600" />
                      </th>
                      <th className="px-4 py-3.5 font-bold w-[9%] sticky right-0 z-10 bg-slate-100 border-l border-slate-200/50 shadow-[-6px_0_12px_-4px_rgba(0,0,0,0.05)]">
                        <ColumnHeader icon={Settings2} label="Action" iconColor="text-purple-600" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {varianceRows.map((row, index) => {
                      // Prefer the backend-computed variance list; fall back to local computation
                      const varianceTypes =
                        row.varianceTypes && row.varianceTypes.length > 0
                          ? row.varianceTypes
                          : computeVarianceTypes(row.consentDataTypes, row.dspmDataTypes);
                      const hasVariance = varianceTypes.length > 0;
                      const isReinitiating = reinitiatingId === row.id;
                      const isSelected = selectedIds.includes(row.id);
                      // Page-aware serial number
                      const serialNo = (page - 1) * PAGE_SIZE + index + 1;

                      return (
                        <tr
                          key={row.id}
                          className={`group border-t border-slate-100/80 transition-colors duration-150 align-top ${isSelected ? "bg-indigo-50/40" : "hover:bg-indigo-50/20"
                            }`}
                        >
                          <td className="px-3 py-4 text-center">
                            <RowCheckbox
                              checked={isSelected}
                              onChange={() => toggleSelectRow(row.id)}
                              ariaLabel={`Select ${row.dataOwner}`}
                            />
                          </td>
                          <td className="px-3 py-4 text-slate-800 font-bold tabular-nums text-center">
                            {serialNo}
                          </td>
                          <td className="px-4 py-4 font-medium text-slate-800 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleOpenLogsDrawer(row.dataOwner)}
                              className="text-indigo-600 hover:text-indigo-850 hover:underline font-semibold cursor-pointer text-left transition-colors duration-150 focus:outline-none"
                            >
                              {row.dataOwner}
                            </button>
                          </td>
                          <td className="px-4 py-4">
                            <SourceTags sources={row.sources} dataOwner={row.dataOwner} />
                          </td>
                          <td className="px-4 py-4">
                            {row.consentType ? (
                              <span className="inline-flex items-center rounded-md font-semibold border bg-blue-50 text-blue-700 border-blue-100 px-2 py-0.5 text-[11px] whitespace-nowrap">
                                {row.consentType}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400 italic">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <DataTypeTags types={row.consentDataTypes} variant="consent" dataOwner={row.dataOwner} />
                          </td>
                          <td className="px-4 py-4">
                            <DataTypeTags types={row.dspmDataTypes} variant="dspm" dataOwner={row.dataOwner} />
                          </td>
                          <td className="px-4 py-4">
                            <DataTypeTags types={varianceTypes} variant="variance" dataOwner={row.dataOwner} />
                          </td>
                          <td className="px-4 py-4">
                            <ReconsentStatusBadge
                              status={row.reconsentStatus}
                              hasDetails={row.deletedTotal !== undefined && row.deletedTotal !== null}
                              onClick={() => handleOpenDeletionModal(row)}
                            />
                          </td>
                          <td className="px-4 py-4 text-xs font-medium text-slate-600 whitespace-nowrap">
                            {formatDateTime(row.createdAt)}
                          </td>
                          <td className={`px-4 py-4 sticky right-0 z-10 border-l border-slate-100/50 shadow-[-6px_0_12px_-4px_rgba(0,0,0,0.05)] transition-colors duration-150 align-top ${isSelected ? "bg-indigo-50/90" : "bg-white group-hover:bg-slate-50"
                            }`}>
                            <ReinitiateVarianceButton
                              disabled={!hasVariance || isReinitiating || row.reconsentStatus === "Initiated"}
                              loading={isReinitiating}
                              status={row.reconsentStatus}
                              onClick={() => handleReinitiateVariance(row)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && varianceRows.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
                <Pagination
                  page={page}
                  pages={pages}
                  total={total}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                  itemLabel="records"
                />
              </div>
            )}
          </div>
        </motion.section>
      </div>

      {/* 50% width activity logs drawer */}
      <AnimatePresence>
        {showLogsDrawer && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowLogsDrawer(false)}
              className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-[2px]"
            />
            {/* Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 26, stiffness: 220 }}
              className="fixed inset-y-0 right-0 z-50 w-full md:w-1/2 max-w-[50vw] bg-white shadow-2xl border-l border-slate-200/80 flex flex-col h-screen overflow-hidden text-slate-900"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex-shrink-0">
                <div>
                  <h3 className="text-base font-extrabold tracking-tight text-slate-900 font-display">
                    Activities History
                  </h3>
                  <p className="text-xs text-slate-550 font-semibold mt-0.5">
                    Data Owner: <span className="text-indigo-600 font-bold">{selectedOwnerForLogs}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowLogsDrawer(false)}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 transition-all focus:outline-none"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Logs Content List */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 no-scrollbar">
                {logsLoading ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
                    <Loader2 size={28} className="animate-spin text-indigo-500" />
                    <span className="text-xs font-semibold">Loading history...</span>
                  </div>
                ) : logsList.length === 0 ? (
                  <div className="text-center py-16 text-slate-400">
                    <p className="text-sm font-semibold">No updates found</p>
                    <p className="text-xs mt-1">History of updates for this data owner will appear here once scans or collections are run.</p>
                  </div>
                ) : (
                  logsList.map((log) => (
                    <LogItem key={log.id} log={log} />
                  ))
                )}
              </div>

              {/* Drawer Footer with Pagination */}
              {!logsLoading && logsList.length > 0 && (
                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end flex-shrink-0">
                  <Pagination
                    page={drawerPage}
                    pages={drawerPages}
                    total={drawerTotal}
                    pageSize={DRAWER_PAGE_SIZE}
                    onPageChange={(p) => {
                      setDrawerPage(p);
                      fetchLogs(selectedOwnerForLogs, p);
                    }}
                    itemLabel="logs"
                  />
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {selectedRowForDeletion && (
        <DeletionDetailsModal
          open={showDeletionModal}
          onClose={() => setShowDeletionModal(false)}
          dataOwner={selectedRowForDeletion.dataOwner}
          deletedTotal={selectedRowForDeletion.deletedTotal}
          deletedByType={selectedRowForDeletion.deletedByType}
          message={selectedRowForDeletion.reconsentStatus}
        />
      )}
    </div>
  );
}
